/**
 * THE ONE PACE PRIMITIVE — src/pace.ts
 *
 *   npx tsx scripts/test-pace.ts
 *
 * Every surface that answers "how fast is this going, and will it make the plan"
 * — the board-wide "Needs attention" read (Layer A), the Gantt's finish-date
 * forecast, the per-milestone "what to do to be on time" actionability, and the
 * cold-start schedule-vs-clock floor — now reads ONE computation here. The lessons
 * ledger records a run of "same milestone, two different numbers" defects
 * (L172/L175/L180/L182b/L183) caused by each surface recomputing throughput /
 * blocked / gap on its own; this module exists so that can never happen again.
 *
 * It is a CONSOLIDATION, not new maths — it composes the already-tested pieces:
 *   - throughput()/forecast()  (src/insights.ts)  — the board's own pace + the
 *     Monte-Carlo p85 finish, holiday-aware, young-board-clamped.
 *   - stillBlocked()           (src/blocked.ts)   — genuinely-waiting cards.
 *   - inFlightCount()          (src/accountability.ts) — in-motion load.
 *   - the canonical status buckets (accountability.ts): to-do = unstarted,
 *     in progress / staging = hands-on, in review = review; backlog is candidate
 *     work, never "open".
 *   - work-calendar.ts         — all working-day maths, never a second iterator.
 *
 * CADENCE is board-wide (the well-sampled rate); the REMAINING scope is local
 * (what this milestone / board still has to clear) — mirroring forecast()'s own
 * (issues, scope) split (insights.ts, 28 Oct).
 *
 * THE SLIP IS THE NUMBER THE SURFACES ALREADY SHOW. `slipDays` is the CALENDAR
 * days the 85%-confident finish (p85) lands past the plan, computed by the exact
 * formula every forecast surface uses today (milestone.tsx `fcTone`, with
 * `planLocalMs` so a plan day is read at LOCAL midnight — the F1 off-by-one fix,
 * ledger A/29 Oct). The old pace contract named this field "gapWorkingDays"; that
 * name is wrong — the shipped surfaces show calendar days, and the hard rule is
 * that pace equals the slip the surfaces show (no second slip math), so parity
 * wins over the name. Working days are used only INTERNALLY, for the lever maths
 * (how many cards a week it needs, how many to drop) where a week means working
 * days. `forecastSlipDays` + `planLocalMs` are exported so the surfaces can import
 * the one copy when they are wired to this primitive.
 */
import type { Issue, IssueStatus } from './types';
import { throughput, forecast } from './insights';
import { stillBlocked } from './blocked';
import { inFlightCount } from './accountability';
import {
  workingDaysInclusive,
  DEFAULT_CALENDAR,
  type WorkCalendar,
} from './work-calendar';

const DAY = 86_400_000;

/** THE CANONICAL PACE WINDOW — the number of weeks every cadence CONCLUSION reads over, so the
 *  finish date, the go-live odds and the Insights trend word can never point opposite each other
 *  (FF-1). pacePicture/forecast default to it; the Insights throughput trend and the keeping-up
 *  finish-rate bind their CONCLUSIONS to it too, even while the user's chosen Insights window
 *  still drives the sparkline span and the shown per-week average. One window for the verdict,
 *  the explorer's own window for the exploration. */
export const PACE_WINDOW_WEEKS = 12;

/** Open = committed work still to do. A card is open unless it is finished
 *  (done), set aside (wontdo), or parked as a candidate (backlog). The canonical
 *  rule, identical to accountability.ts `isOpen` (:130) — a backlog card is the
 *  holding pen, never "open" (Vinnie, 30 Oct; shipped 3.33). */
const isOpenStatus = (s: IssueStatus): boolean =>
  s !== 'done' && s !== 'wontdo' && s !== 'backlog';

/** A plain YYYY-MM-DD as its LOCAL-midnight instant — the app's one convention for
 *  a plan day (milestone-forecast `planMs`, ProjectStatus `planMsOf`). Parsing it
 *  as UTC made the slip read a day high west of UTC and disagree with the "N days
 *  late" headline (F1 / ledger A, 29 Oct). The canonical copy lives here now; the
 *  surfaces (milestone.tsx) import it from pace when they are wired in. */
export const planLocalMs = (iso: string): number => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getTime();
};

/** The CALENDAR days the 85%-confident finish lands past a plan day — the slip
 *  every forecast surface shows (milestone.tsx `fcTone`/`predSlip`). Positive =
 *  late. The ONE slip computation; surfaces import this rather than re-deriving
 *  it, so a board can never read two different "days late" (the L172/L183 class). */
export const forecastSlipDays = (p85Ms: number, plannedDay: string): number =>
  Math.round((p85Ms - planLocalMs(plannedDay)) / DAY);

/** A plain local YYYY-MM-DD for an instant — the same local-midnight convention as
 *  planLocalMs, so a working-day count from "now" to a plan day is measured on the
 *  calendar the user sees, not UTC. */
function localDayISO(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export interface PacePicture {
  /** Committed work still to do in scope (excludes backlog, done, wontdo). */
  openCount: number;
  /** Of scope, how many are genuinely waiting on something (stillBlocked). */
  blockedCount: number;
  /** Of scope, how many are committed but not yet started (status = to-do). */
  unstartedCount: number;
  /** In-motion load: in progress + in review, less the blocked-in-flight. */
  inFlightCount: number;
  /** The board's own throughput, cards per week (board-wide cadence). */
  paceNow: number;
  /** Enough finished history to stand behind a forecast. When false the forecast
   *  levers below are withheld (null) — a confident date is never shown on thin
   *  data (the EVM-15%-floor discipline, extended to pace). */
  ready: boolean;
  /** The 85%-confident finish instant, holiday-aware; null until `ready`. */
  p85Ms: number | null;
  /** CALENDAR days p85 lands past the plan (positive = late) — the slip the
   *  surfaces show. Null when not ready or no plan to judge against. */
  slipDays: number | null;
  /** Throughput (cards/week in WORKING weeks) still needed to hit the plan; null
   *  when not ready or the plan day is already here/past. */
  paceNeeded: number | null;
  /** How many cards to move out of scope to still land by the plan at today's
   *  pace; null when not ready. */
  cardsToDropToHitPlan: number | null;
}

/**
 * The one pace read for a scope against a plan.
 *
 * @param allIssues the whole board — the CADENCE (throughput/forecast) is measured
 *   board-wide, because that is the well-sampled rate.
 * @param scope the work this read is about — a milestone's gated cards, or the
 *   whole board (pass scope === allIssues). The REMAINING count is scope-local.
 * @param now epoch ms.
 * @param planDay the plan day as YYYY-MM-DD (a milestone's planned day, or the
 *   go-live), or null when there is nothing to be late against.
 * @param cal the workspace working calendar (weekends + holidays). Default = the
 *   plain Mon–Fri, no-holiday calendar.
 * @param weeks pace window in weeks (default 12).
 * @param countBacklog mode-keyed (Vinnie, 30 Oct): a project counts its backlog as
 *   planned work still to deliver (true, the default) so it forecasts honestly; a
 *   continuous board leaves it out (false). Same flag feeds the Dashboard %, so the
 *   two never disagree. Note `openCount` ALWAYS excludes backlog regardless — the
 *   backlog counts toward the forecast's remaining scope, never toward "open".
 */
export function pacePicture(
  allIssues: Issue[],
  scope: Issue[],
  now: number,
  planDay: string | null | undefined,
  cal: WorkCalendar = DEFAULT_CALENDAR,
  weeks = PACE_WINDOW_WEEKS,
  countBacklog = true,
): PacePicture {
  /* One pass over the scope for the committed-work counts, using the canonical
     status buckets (accountability.ts): to-do = unstarted; in progress / staging =
     hands-on; in review = review. A blocked card counts against in-flight only when
     it is itself in an in-flight lane (it was in motion) — the same rule the
     overload meter uses (accountability.ts toRow). */
  let inProgress = 0;
  let inReview = 0;
  let unstarted = 0;
  let open = 0;
  let blocked = 0;
  let blockedInFlight = 0;
  for (const i of scope) {
    const s = i.status;
    if (isOpenStatus(s)) open += 1;
    if (s === 'inprogress' || s === 'staging') inProgress += 1;
    else if (s === 'inreview') inReview += 1;
    else if (s === 'todo') unstarted += 1;
    /* Blocker refs resolve against the WHOLE board (a blocker may be a card outside
       this scope), so judge stillBlocked against allIssues, count within scope. */
    if (stillBlocked(i, allIssues)) {
      blocked += 1;
      if (s === 'inprogress' || s === 'staging' || s === 'inreview') blockedInFlight += 1;
    }
  }
  const inFlight = inFlightCount(inProgress, inReview, blockedInFlight);

  /* CADENCE — board-wide throughput, clamped to the board's own life so a young
     board is not read as slow for the weeks before it existed (the same firstSeen
     clamp forecast() applies — insights.ts). */
  const firstSeen = allIssues.reduce(
    (m, i) => (i.createdAt != null && i.createdAt < m ? i.createdAt : m),
    Infinity,
  );
  const paceNow = throughput(
    allIssues,
    now,
    weeks,
    Number.isFinite(firstSeen) ? firstSeen : undefined,
  ).perWeek;

  /* THE FORECAST — board cadence consuming the scope's remaining work, holiday-
     aware, mode-keyed backlog. One call; its p85 and `ready` are the source of the
     date and the trust gate for every surface. */
  const planMs = planDay ? planLocalMs(planDay) : null;
  const fc = forecast(allIssues, now, planMs, weeks, undefined, undefined, 'pace', scope, cal, countBacklog);
  const ready = fc.ready;
  const p85Ms = ready ? fc.p85 : null;
  const slipDays = ready && planDay ? forecastSlipDays(fc.p85, planDay) : null;

  /* THE LEVERS — what it would take to still hit the plan. Withheld entirely until
     there is enough history to forecast (`ready`), and measured in WORKING weeks
     (the working days from now to the plan over the working-week length). When the
     plan day is already here or past there is no runway: the pace needed is
     undefined and everything remaining is already past the plan. */
  let paceNeeded: number | null = null;
  let cardsToDropToHitPlan: number | null = null;
  if (ready && planDay) {
    const workingDaysLeft = workingDaysInclusive(localDayISO(now), planDay, cal);
    const weekLen = cal.week && cal.week.length ? cal.week.length : 5;
    const weeksLeft = workingDaysLeft / weekLen;
    if (weeksLeft > 0) {
      paceNeeded = Math.round((fc.remaining / weeksLeft) * 10) / 10;
      cardsToDropToHitPlan = Math.max(0, Math.round(fc.remaining - paceNow * weeksLeft));
    } else {
      /* No runway left: the plan day is here/past, so it cannot be hit by pace —
         the whole remaining scope is already beyond the plan. */
      cardsToDropToHitPlan = fc.remaining;
    }
  }

  return {
    openCount: open,
    blockedCount: blocked,
    unstartedCount: unstarted,
    inFlightCount: inFlight,
    paceNow,
    ready,
    p85Ms,
    slipDays,
    paceNeeded,
    cardsToDropToHitPlan,
  };
}

/**
 * "What to do to be on time" for ONE milestone (or the go-live) — the 7-field
 * actionability read from docs/milestone-mechanism-spec.md §3, as a thin wrapper
 * over pacePicture so the per-milestone read and the board-wide read are the SAME
 * computation, never two. `gapDays` is pace's `slipDays` (the calendar-day slip
 * the surfaces show).
 */
export interface MilestoneActionability {
  gapDays: number | null;
  openCount: number;
  blockedCount: number;
  unstartedCount: number;
  paceNow: number;
  paceNeeded: number | null;
  cardsToDropToHitPlan: number | null;
}

export function milestoneActionability(
  allIssues: Issue[],
  scope: Issue[],
  now: number,
  planDay: string | null | undefined,
  cal: WorkCalendar = DEFAULT_CALENDAR,
  weeks = PACE_WINDOW_WEEKS,
  countBacklog = true,
): MilestoneActionability {
  const p = pacePicture(allIssues, scope, now, planDay, cal, weeks, countBacklog);
  return {
    gapDays: p.slipDays,
    openCount: p.openCount,
    blockedCount: p.blockedCount,
    unstartedCount: p.unstartedCount,
    paceNow: p.paceNow,
    paceNeeded: p.paceNeeded,
    cardsToDropToHitPlan: p.cardsToDropToHitPlan,
  };
}

/* THE SCHEDULE-VS-CLOCK FLOOR — re-exported so pace is the ONE place a surface
 * reads the progress-vs-clock severity from, resolving the `TODO(pace.ts)` note in
 * progress-health.ts. The implementation stays in progress-health (where it is
 * tested and where its ScheduleHealth input is built); pace does not re-derive it
 * — it exposes it, so "the progress-vs-clock severity and the Monte-Carlo forecast
 * draw from one source" without the floor living in two places. */
export { scheduleHealthSeverity } from './progress-health';
export type { ScheduleHealth } from './progress-health';
